/**
 * In-memory, single-slot, single-use channel for handing the response of a
 * request that was REPLAYED after a federated (SSO) step-up redirect back to
 * the feature component that originally issued it.
 *
 * Why this exists: a federated step-up is a full-page redirect round-trip, so
 * the original caller's in-memory state (e.g. a React mutation's resolve path)
 * is destroyed. The resume hook replays the persisted request and publishes the
 * fresh response here; the originating page picks it up on mount/event.
 *
 * SECURITY: the body may contain a one-time plaintext secret (a newly created
 * API key). It is held in memory ONLY — never written to session/localStorage —
 * and auto-cleared after `ttlMs` if no consumer takes it.
 */
export interface ResumedResult {
  url: string;
  method: string;
  status: number;
  ok: boolean;
  body: unknown;
}

let buffered: ResumedResult | null = null;
let timer: ReturnType<typeof setTimeout> | undefined;

function clearTimer(): void {
  if (timer) {
    clearTimeout(timer);
    timer = undefined;
  }
}

/** Publish the result of a replayed federated-step-up request. */
export function publishResumedResult(result: ResumedResult, ttlMs = 10_000): void {
  buffered = result;
  clearTimer();
  timer = setTimeout(() => {
    buffered = null;
    timer = undefined;
  }, ttlMs);
  // Don't keep a Node event loop alive solely for this timer (no-op in browsers).
  (timer as unknown as { unref?: () => void }).unref?.();
}

/** Single-use read: returns the buffered result (if any) and clears it. */
export function takeResumedResult(): ResumedResult | null {
  const r = buffered;
  buffered = null;
  clearTimer();
  return r;
}
